import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom/client";
import { createPortal } from "react-dom";
import { gsap } from "gsap";

const colorPalette = [
	{ border: "#6366F1", gradient: "linear-gradient(145deg, #4F46E5, #080c16 75%)", glow: "rgba(99, 102, 241, 0.4)" },
	{ border: "#10B981", gradient: "linear-gradient(145deg, #059669, #05140f 75%)", glow: "rgba(16, 185, 129, 0.4)" },
	{ border: "#8B5CF6", gradient: "linear-gradient(145deg, #7C3AED, #0e0719 75%)", glow: "rgba(139, 92, 246, 0.4)" },
	{ border: "#F59E0B", gradient: "linear-gradient(145deg, #D97706, #160f04 75%)", glow: "rgba(245, 158, 11, 0.4)" },
	{ border: "#EC4899", gradient: "linear-gradient(145deg, #DB2777, #180612 75%)", glow: "rgba(236, 72, 153, 0.4)" },
	{ border: "#06B6D4", gradient: "linear-gradient(145deg, #0891B2, #041217 75%)", glow: "rgba(6, 182, 212, 0.4)" },
	{ border: "#3B82F6", gradient: "linear-gradient(145deg, #2563EB, #050b18 75%)", glow: "rgba(59, 130, 246, 0.4)" }
];

function getInitials(name = "") {
	const parts = name.trim().split(/\s+/).filter(Boolean);
	if (!parts.length) return "S";
	if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
	return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function ChromaGrid({
	items = [],
	className = "",
	radius = 300,
	columns = 3,
	rows = 2,
	damping = 0.45,
	fadeOut = 0.6,
	ease = "power3.out"
}) {
	const rootRef = useRef(null);
	const fadeRef = useRef(null);
	const setX = useRef(null);
	const setY = useRef(null);
	const pos = useRef({ x: 0, y: 0 });
	const [activeMember, setActiveMember] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const pageSize = 9;
	const pageCount = Math.ceil(items.length / pageSize);
	const pagedItems = items.slice((currentPage - 1) * pageSize, currentPage * pageSize);

	useEffect(() => {
		const el = rootRef.current;
		if (!el) return;
		setX.current = gsap.quickSetter(el, "--x", "px");
		setY.current = gsap.quickSetter(el, "--y", "px");
		const { width, height } = el.getBoundingClientRect();
		pos.current = { x: width / 2, y: height / 2 };
		setX.current(pos.current.x);
		setY.current(pos.current.y);
	}, []);

	useEffect(() => {
		if (!activeMember) return undefined;
		const previousOverflow = document.body.style.overflow;
		const handleKeyDown = (event) => {
			if (event.key === "Escape") setActiveMember(null);
		};
		document.body.style.overflow = "hidden";
		window.addEventListener("keydown", handleKeyDown);
		return () => {
			document.body.style.overflow = previousOverflow;
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [activeMember]);

	const moveTo = (x, y) => {
		gsap.to(pos.current, {
			x,
			y,
			duration: damping,
			ease,
			onUpdate: () => {
				setX.current?.(pos.current.x);
				setY.current?.(pos.current.y);
			},
			overwrite: true
		});
	};

	const handleMove = (e) => {
		if (!rootRef.current) return;
		const r = rootRef.current.getBoundingClientRect();
		moveTo(e.clientX - r.left, e.clientY - r.top);
		if (fadeRef.current) {
			gsap.to(fadeRef.current, { opacity: 0, duration: 0.25, overwrite: true });
		}
	};

	const handleLeave = () => {
		if (fadeRef.current) {
			gsap.to(fadeRef.current, {
				opacity: 1,
				duration: fadeOut,
				overwrite: true
			});
		}
	};

	const handleCardMove = (e) => {
		const card = e.currentTarget;
		const rect = card.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;
		card.style.setProperty("--mouse-x", `${x}px`);
		card.style.setProperty("--mouse-y", `${y}px`);
	};

	const changePage = (page) => {
		setCurrentPage(page);
		requestAnimationFrame(() => rootRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }));
	};

	return (
		<>
		<div
			ref={rootRef}
			className={`chroma-grid ${className}`}
			style={{
				"--r": `${radius}px`,
				"--cols": columns,
				"--rows": rows
			}}
			onPointerMove={handleMove}
			onPointerLeave={handleLeave}
			role="list"
		>
			{pagedItems.map((item, index) => (
				<article
					key={item.id || item.title || index}
					className="chroma-card"
					onMouseMove={handleCardMove}
					onClick={() => setActiveMember(item)}
					onKeyDown={(e) => {
						if (e.key === "Enter" || e.key === " ") {
							e.preventDefault();
							setActiveMember(item);
						}
					}}
					style={{
						"--card-border": item.borderColor || "#6366F1",
						"--card-gradient": item.gradient
					}}
					tabIndex={0}
					role="listitem"
					aria-label={`${item.title} - ${item.subtitle}`}
				>
					<div className="chroma-img-wrapper">
						{item.image ? (
							<img
								src={item.image}
								alt={item.alt || item.title}
								loading="lazy"
								onError={(e) => {
									e.currentTarget.style.display = "none";
									const fallback = e.currentTarget.parentElement?.querySelector(".chroma-avatar-fallback");
									if (fallback) fallback.style.display = "flex";
								}}
							/>
						) : null}
						<div
							className="chroma-avatar-fallback"
							style={{ display: item.image ? "none" : "flex" }}
						>
							<div className="chroma-avatar-badge">
								<span className="chroma-avatar-initials">{getInitials(item.title)}</span>
							</div>
							<span className="chroma-avatar-pill">{item.handle || "Solufy Lead"}</span>
						</div>
					</div>
					<footer className="chroma-info">
						<div className="header-row">
							<h3 className="name">{item.title}</h3>
							{item.handle && <span className="handle">{item.handle}</span>}
						</div>
						<p className="role">{item.subtitle}</p>
						<span className="card-cta">
							<span>View Profile</span>
							<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
								<path d="M5 12h14"></path>
								<path d="m12 5 7 7-7 7"></path>
							</svg>
						</span>
					</footer>
				</article>
			))}
			<div className="chroma-overlay" aria-hidden="true" />
			<div ref={fadeRef} className="chroma-fade" aria-hidden="true" />
			{activeMember ? (
				<TeamMemberModal member={activeMember} onClose={() => setActiveMember(null)} />
			) : null}
		</div>
		{pageCount > 1 ? (
			<nav className="solufy-pagination" aria-label="Team pagination">
				<button type="button" disabled={currentPage === 1} onClick={() => changePage(currentPage - 1)}>←</button>
				{Array.from({ length: pageCount }, (_, index) => index + 1).map((page) => (
					<button type="button" key={page} aria-current={page === currentPage ? "page" : undefined} onClick={() => changePage(page)}>{page}</button>
				))}
				<button type="button" disabled={currentPage === pageCount} onClick={() => changePage(currentPage + 1)}>→</button>
			</nav>
		) : null}
		</>
	);
}

function TeamMemberModal({ member, onClose }) {
	const whatsappDigits = (member.whatsapp || "").replace(/[^0-9]/g, "");
	const [imgFailed, setImgFailed] = useState(false);

	return createPortal(
		<div
			className="solufy-team-modal"
			style={{ "--member-accent": member.borderColor || "#3b82f6" }}
			role="presentation"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			<section
				className="solufy-team-modal__dialog"
				role="dialog"
				aria-modal="true"
				aria-labelledby="solufy-team-modal-name"
			>
				<button
					type="button"
					className="solufy-team-modal__close"
					onClick={onClose}
					aria-label="Close member details"
				>
					×
				</button>
				<div className="solufy-team-modal__photo">
					{member.image && !imgFailed ? (
						<img
							src={member.image}
							alt={member.alt || member.title}
							onError={() => setImgFailed(true)}
						/>
					) : (
						<div className="modal-avatar-fallback">
							<div className="modal-avatar-badge">
								<span>{getInitials(member.title)}</span>
							</div>
							<span className="modal-avatar-handle">{member.handle || "Solufy Team"}</span>
						</div>
					)}
				</div>
				<div className="solufy-team-modal__content">
					<span className="solufy-team-modal__department">{member.handle}</span>
					<h3 id="solufy-team-modal-name">{member.title}</h3>
					<strong>{member.subtitle}</strong>
					<p>
						{member.description ||
							"A dedicated member of the Solufy team, delivering reliable ERPNext and Frappe solutions for global enterprise clients."}
					</p>
					<div className="solufy-team-modal__links">
						{member.linkedin ? (
							<a href={member.linkedin} target="_blank" rel="noopener noreferrer">
								<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
									<path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.46 10.9h2.79v8.37H6.46v-8.37M7.86 6.3a1.62 1.62 0 1 0 0 3.24 1.62 1.62 0 0 0 0-3.24Z" />
								</svg>
								Connect on LinkedIn ↗
							</a>
						) : null}
						{member.instagram ? (
							<a href={member.instagram} target="_blank" rel="noopener noreferrer">
								Instagram ↗
							</a>
						) : null}
						{whatsappDigits ? (
							<a href={`https://wa.me/${whatsappDigits}`} target="_blank" rel="noopener noreferrer">
								<svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
									<path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2m.01 1.67c2.2 0 4.26.86 5.82 2.42a8.225 8.225 0 0 1 2.41 5.83c0 4.54-3.7 8.24-8.24 8.24-1.44 0-2.85-.38-4.09-1.11l-.29-.17-3.04.8 1.2-2.96-.19-.3a8.212 8.212 0 0 1-1.26-4.49c0-4.54 3.7-8.24 8.24-8.24m4.52 11.58c-.25-.13-1.47-.72-1.7-.8-.23-.09-.39-.13-.56.13-.17.25-.64.8-.79.96-.14.17-.29.19-.54.06-.25-.13-1.06-.39-2.02-1.25-.75-.67-1.26-1.5-1.4-1.75-.15-.25-.02-.39.11-.51.11-.11.25-.29.38-.44.13-.14.17-.25.25-.42.08-.17.04-.31-.02-.44-.06-.13-.56-1.34-.76-1.84-.2-.49-.4-.42-.56-.43h-.48c-.17 0-.44.06-.67.31-.23.25-.88.86-.88 2.1 0 1.24.9 2.44 1.03 2.61.13.17 1.77 2.7 4.29 3.78.6.26 1.07.41 1.44.53.6.19 1.15.16 1.58.1.48-.07 1.47-.6 1.68-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.16-.48-.28Z" />
								</svg>
								WhatsApp ↗
							</a>
						) : null}
					</div>
				</div>
			</section>
		</div>,
		document.body
	);
}

export function mountChromaGrid(root = document) {
	const containers = root.querySelectorAll("#solufy-team-chroma-root");
	containers.forEach((container) => {
		if (!container || container.dataset.mounted) return;
		const data = container.parentElement
			? container.parentElement.querySelector("#solufy-team-data")
			: root.querySelector("#solufy-team-data");
		if (!data) return;

		let members;
		try {
			members = JSON.parse(data.textContent || "[]");
		} catch (error) {
			console.warn("Unable to parse team data", error);
			return;
		}
		if (!members || !members.length) return;

		const isTeamPage = Boolean(
			document.querySelector(".solufy-team-page") || container.closest(".solufy-team-page")
		);
		const columns = isTeamPage ? 4 : 3;

		const items = members.map((member, index) => {
			const palette = colorPalette[index % colorPalette.length];
			return {
				id: member.name,
				image: member.photo,
				alt: member.photo_alt_text || member.full_name,
				title: member.full_name,
				subtitle: member.designation || "Solution Specialist",
				handle: member.department || "Frappe & ERPNext",
				description: member.short_bio,
				linkedin: member.linkedin_url,
				instagram: member.instagram_url,
				whatsapp: member.whatsapp_number,
				borderColor: palette.border,
				gradient: palette.gradient,
				glow: palette.glow
			};
		});

		container.dataset.mounted = "true";
		ReactDOM.createRoot(container).render(
			<ChromaGrid
				items={items}
				columns={columns}
				radius={300}
				damping={0.45}
				fadeOut={0.6}
				ease="power3.out"
			/>
		);
	});
}
